import { useTranslation } from 'react-i18next'
import { H4, Text } from '@/components/atoms'
import { STORE_URLS } from '@/shared/hooks/use-app-version-flag'
import { appStoreBadgeCoral, googlePlayBadgeCoral } from '@/shared/assets'
import { useAppDownloadBanner } from './app-download-banner.hook'

export type AppDownloadBannerVariant = 'wide' | 'compact'

interface AppDownloadBannerProps {
  variant?: AppDownloadBannerVariant
  className?: string
}

const VARIANTS: Record<
  AppDownloadBannerVariant,
  {
    card: string
    text: string
    title: string
    description: string
    badges: string
    badge: string
  }
> = {
  wide: {
    card: 'gap-6 p-6 @3xl:flex-row @3xl:justify-between @3xl:px-8',
    text: '@3xl:min-w-0 @3xl:flex-1 @3xl:text-left',
    title: 'leading-7!',
    description: 'leading-5! tracking-[0.01rem]',
    badges: '@md:flex-row',
    badge: 'h-10',
  },
  compact: {
    card: 'h-full gap-2 px-6 py-4',
    text: '',
    title: 'text-xl! leading-6!',
    description: 'text-sm! leading-4.5! tracking-normal! text-balance',
    badges: 'flex-row',
    badge: 'h-9',
  },
}

export function AppDownloadBanner({
  variant = 'wide',
  className = '',
}: AppDownloadBannerProps) {
  const config = VARIANTS[variant]
  const { t } = useTranslation()
  const { isHidden, showAppStore, showGooglePlay } = useAppDownloadBanner()

  if (isHidden) return <></>

  return (
    <div
      className={`@container w-full ${className}`}
      data-testid="app-download-banner"
    >
      <div
        className={`border-coral-100 bg-coral-50 flex flex-col items-center justify-center rounded-2xl border ${config.card}`}
      >
        <div className={`flex flex-col gap-2 text-center ${config.text}`}>
          <H4 className={`text-neutral-900 ${config.title}`}>
            {t('appDownloadBanner.title')}
          </H4>
          <Text className={`text-neutral-900 ${config.description}`}>
            {t('appDownloadBanner.description')}
          </Text>
        </div>

        <div
          className={`flex shrink-0 flex-col items-center gap-6 ${config.badges}`}
        >
          <div className="flex shrink-0 items-center gap-4">
            {showAppStore && (
              <a
                href={STORE_URLS.ios}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="app-download-banner-app-store"
              >
                <img
                  src={appStoreBadgeCoral}
                  alt={t('appDownloadBanner.appStoreAlt')}
                  className={config.badge}
                />
              </a>
            )}
            {showGooglePlay && (
              <a
                href={STORE_URLS.android}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="app-download-banner-google-play"
              >
                <img
                  src={googlePlayBadgeCoral}
                  alt={t('appDownloadBanner.googlePlayAlt')}
                  className={config.badge}
                />
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
